import apiClient from '@/services/axios'

import uuidv1 from 'uuid/v1'

const HEADER_FIELDS = {
  image: {
    url: '',
    id: null,
    file: null,
    loading: false,
  },
  buttons: [],
}

function findBlockById(
  blocks,
  value,
  key = 'id',
  returnParent = false,
  parent = null,
) {
  if (!Array.isArray(blocks)) return null

  for (const block of blocks) {
    if (block[key] === value) {
      if (!returnParent) {
        return block
      }

      return { block, parent }
    }

    if (block.children && block.children.length > 0) {
      const found = findBlockById(
        block.children,
        value,
        key,
        returnParent,
        block,
      )
      if (found) return found
    }
  }
  return null
}

const getDefaultState = () => ({
  header: {
    ru: {
      image: {
        url: '',
        id: null,
        file: null,
        loading: false,
      },
      buttons: [],
    },
  },
  content: {
    blocks: {
      ru: [],
    },
  },
  sports: [], // sports
  championshipsBySport: {}, // { [sportId]: [] }
  matchesByChamp: {}, // { [champId]: [] }
  selectedLanguage: 'ru',
  isHeaderEditModalOpen: false,
  isCategoryModalVisible: false,
  isWidgetModalVisible: false,
  isEventsModalVisible: false,
  selectedCategoryData: null,
  selectedWidgetData: null,
})

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations: {
    SET_SELECTED_LANGUAGE(state, lang) {
      state.selectedLanguage = lang
    },

    SET_SPORTS(state, sports) {
      state.sports = sports
    },
    SET_CHAMPIONSHIPS(state, { sportId, champs }) {
      state.championshipsBySport = {
        ...state.championshipsBySport,
        [sportId]: champs,
      }
    },
    SET_MATCHES(state, { champId, matches }) {
      state.matchesByChamp = {
        ...state.matchesByChamp,
        [champId]: matches,
      }
    },

    SET_HEADER(state, header) {
      state.header = JSON.parse(JSON.stringify(header))
    },
    SET_HEADER_EDIT_MODAL_VISIBLE(state, visible) {
      state.isHeaderEditModalOpen = visible
    },

    SET_CATEGORY_MODAL_VISIBLE(state, visible) {
      state.isCategoryModalVisible = visible
    },
    SET_WIDGET_MODAL_VISIBLE(state, visible) {
      state.isWidgetModalVisible = visible
    },
    SET_EVENTS_MODAL_VISIBLE(state, visible) {
      state.isEventsModalVisible = visible
    },
    SET_SELECTED_CATEGORY_DATA(state, data) {
      state.selectedCategoryData = data
    },
    SET_SELECTED_WIDGET_DATA(state, data) {
      state.selectedWidgetData = data
    },
    SET_EVENTS(state, events) {
      state.content.events = events
    },
    SET_BLOCKS(state, blocks) {
      state.content.blocks = JSON.parse(JSON.stringify(blocks))
    },
    RESET_STATE(state) {
      Object.assign(state, getDefaultState())
    },
  },
  actions: {
    setSelectedLanguage({ commit }, lang) {
      commit('SET_SELECTED_LANGUAGE', lang)
    },

    async fetchSports({ state, commit }) {
      if (state.sports.length) return state.sports

      try {
        const res = await apiClient.get('/admin/custom-page/sports')
        const sports =
          res?.data?.data.map((s) => ({ ...s, type: 'sport' })) || []
        commit('SET_SPORTS', sports)
        return sports
      } catch (e) {
        this.$notification.error({
          message: 'Error while fetching sports',
          description: e.message,
        })
      }
    },

    async fetchChampionships({ state, commit }, sportId) {
      if (state.championshipsBySport[sportId]) {
        return state.championshipsBySport[sportId]
      }

      try {
        const res = await apiClient.get(
          `/admin/custom-page/championships?sport_id=${sportId}`,
        )
        const champs =
          res?.data?.data.map((c) => ({ ...c, type: 'champ' })) || []
        commit('SET_CHAMPIONSHIPS', { sportId, champs })
        return champs
      } catch (e) {
        this.$notification.error({
          message: 'Error while fetching sports',
          description: e.message,
        })
      }
    },

    async fetchMatches({ state, commit }, { sportId, champId }) {
      if (state.matchesByChamp[champId]) {
        return state.matchesByChamp[champId]
      }

      try {
        const res = await apiClient.get(
          `/admin/custom-page/matches?champ_id=${champId}&sport_id=${sportId}`,
        )
        const matches = res?.data?.data || []
        commit('SET_MATCHES', { champId, matches })
        return matches
      } catch (e) {
        this.$notification.error({
          message: 'Error while fetching sports',
          description: e.message,
        })
      }
    },

    async initForm({ commit, dispatch, rootGetters }) {
      await dispatch('getLangs', null, { root: true })

      const langs = rootGetters['langs']?.langData

      if (langs.length === 0) {
        commit('SET_HEADER', {
          ru: { ...HEADER_FIELDS },
        })

        return
      }

      const header = langs.reduce((acc, lang) => {
        return {
          ...acc,
          [lang.name]: { ...HEADER_FIELDS },
        }
      }, {})

      commit('SET_HEADER', header)
    },

    async fillHeader({ commit, state, dispatch, rootGetters }, data) {
      if (!data.header?.langs) {
        return
      }

      await dispatch('getLangs', null, { root: true })

      const langs = rootGetters['langs']?.langData

      const copyHeader = JSON.parse(JSON.stringify(state.header))

      const header = {}

      // Banner
      data.header.langs.forEach((banner) => {
        const langName = langs.find((l) => banner.lid === l.id).name

        if (!header[langName]) {
          header[langName] = { buttons: [] }
        }

        header[langName].image = {
          url: banner.banner_background_path,
          id: banner.banner_background_id,
          file: null,
          loading: false,
        }
      })

      // eslint-disable-next-line no-unused-expressions
      data.header.buttons?.forEach((button) => {
        // eslint-disable-next-line no-unused-expressions
        button?.langs?.forEach((buttonContent) => {
          const langName = langs.find((l) => buttonContent.lid === l.id).name

          if (!header[langName].buttons) {
            header[langName].buttons = []
          }

          header[langName].buttons.push({
            id: uuidv1(),
            title: buttonContent.title,
            deeplink: buttonContent.link,
          })
        })
      })

      commit('SET_HEADER', { ...copyHeader, ...header })
    },
    updateHeader({ commit, state }, data) {
      const headerCopy = JSON.parse(JSON.stringify(state.header))
      Object.entries(data).forEach(([key, value]) => {
        headerCopy[key] = {
          ...value,
          buttons: value.buttons?.map((button) => ({
            id: button.id,
            title: button.title,
            deeplink: button.deeplink,
          })),
        }
      })
      commit('SET_HEADER', headerCopy)
      commit('SET_HEADER_EDIT_MODAL_VISIBLE', false)
    },
    setHeaderEditModalVisible({ commit }, visible) {
      commit('SET_HEADER_EDIT_MODAL_VISIBLE', visible)
    },

    async fillBlocks({ commit, dispatch, rootGetters }, data) {
      await dispatch('getLangs', null, { root: true })

      if (!rootGetters['sportsSearchCache']?.['']) {
        await dispatch('fetchSports', null, { root: true })
      }

      const langs = rootGetters['langs']?.langData
      const icons = rootGetters['sportsSearchCache']?.['']

      const blocks = {}

      data.blocks.forEach((block) => {
        const blockId = uuidv1()

        // eslint-disable-next-line no-unused-expressions
        block.langs?.forEach((content) => {
          const langName = langs.find((l) => content.lid === l.id)?.name
          if (!langName) return

          if (!blocks[langName]) blocks[langName] = []

          const selectedIcon = icons?.find((i) => i.id === content.icon_id)

          if (block.type === 'category') {
            const categoryBlock = {
              type: block.type,
              id: blockId,
              title: content.title,
              iconField: selectedIcon?.name ?? null,
              selectedIcon: selectedIcon ?? null,
              iconColor: content.icon_color,
              showTournaments: content.show_championship,
              children: [],
            }

            if (block.items) {
              block.items.forEach((item) => {
                const itemId = uuidv1()

                if (item.type === 'champ') {
                  item.langs
                    .filter((lang) => lang.lid === content.lid)
                    .forEach((lang) => {
                      const champBlock = {
                        type: 'tournament',
                        id: itemId,
                        championshipId: lang.championship_id,
                        name: lang.championship_name,
                        children: [],
                      }

                      if (item.items) {
                        item.items.forEach((subItem) => {
                          const subItemId = uuidv1()

                          if (subItem.type === 'banner') {
                            subItem.langs
                              .filter((l) => l.lid === content.lid)
                              .forEach((l) => {
                                champBlock.children.push({
                                  type: 'widget',
                                  id: subItemId,
                                  name: l.title,
                                  backgroundImageId: l.background_image_id,
                                  backgroundImageUrl: l.background_image_path,
                                  backgroundImageFile: null,
                                  linkType: l.link_type,
                                  link: l.link_type === 'sport' ? [] : l.link,
                                  sportId:
                                    l.link_type === 'sport'
                                      ? Number(l.link)
                                      : null,
                                  showInfo: !!l.show_info_icon,
                                  theme: l.theme,
                                  infoLink: l.info_link,
                                })
                              })
                          }

                          if (subItem.type === 'event') {
                            subItem.langs
                              .filter((l) => l.lid === content.lid)
                              .forEach((l) => {
                                champBlock.children.push({
                                  type: 'match',
                                  id: l.match_id,
                                  name: l.match_name,
                                  date: l.match_starts_at,
                                  championshipId: l.championship_id,
                                  championshipName: l.championship_name,
                                  sportId: l.sport_id,
                                  matchId: l.match_id,
                                })
                              })
                          }
                        })
                      }

                      categoryBlock.children.push(champBlock)
                    })
                }

                if (item.type === 'banner') {
                  item.langs
                    .filter((lang) => lang.lid === content.lid)
                    .forEach((lang) => {
                      categoryBlock.children.push({
                        type: 'widget',
                        id: itemId,
                        name: lang.title,
                        backgroundImageId: lang.background_image_id,
                        backgroundImageUrl: lang.background_image_path,
                        backgroundImageFile: null,
                        linkType: lang.link_type,
                        link: lang.link_type === 'sport' ? [] : lang.link,
                        sportId:
                          lang.link_type === 'sport' ? Number(lang.link) : null,
                        showInfo: !!lang.show_info_icon,
                        theme: lang.theme,
                        infoLink: lang.info_link,
                      })
                    })
                }

                if (item.type === 'event') {
                  item.langs
                    .filter((lang) => lang.lid === content.lid)
                    .forEach((lang) => {
                      categoryBlock.children.push({
                        type: 'match',
                        id: lang.match_id,
                        name: lang.match_name,
                        date: lang.match_starts_at,
                        championshipId: lang.championship_id,
                        championshipName: lang.championship_name,
                        sportId: lang.sport_id,
                        matchId: lang.match_id,
                      })
                    })
                }
              })
            }

            blocks[langName].push(categoryBlock)
          }

          if (block.type === 'banner') {
            blocks[langName].push({
              type: 'widget',
              id: blockId,
              name: content.title,
              backgroundImageId: content.background_image_id,
              backgroundImageUrl: content.background_image_path,
              backgroundImageFile: null,
              linkType: content.link_type,
              link: content.link_type === 'sport' ? [] : content.link,
              sportId:
                content.link_type === 'sport' ? Number(content.link) : null,
              showInfo: !!content.show_info_icon,
              theme: content.theme,
              infoLink: content.info_link,
            })
          }
        })
      })

      commit('SET_BLOCKS', blocks)
    },

    removeChampFromBlocks({ commit, state }, block) {
      const blocksCopy = JSON.parse(JSON.stringify(state.content.blocks))
      const currentLanguage = state.selectedLanguage

      const result = findBlockById(
        blocksCopy[currentLanguage],
        block.championshipId,
        'championshipId',
        true,
      )

      if (!result) return

      const { block: tournament, parent } = result

      if (!tournament?.children?.length && parent) {
        parent.children = parent.children.filter((c) => c !== tournament)
      }

      commit('SET_BLOCKS', blocksCopy)
    },

    onCategorySubmit({ commit, state }, { data, id }) {
      const blocksCopy = JSON.parse(JSON.stringify(state.content.blocks))

      const blockId = uuidv1()
      Object.entries(data).forEach(([key, value]) => {
        if (!blocksCopy[key]) {
          blocksCopy[key] = []
        }

        if (id) {
          const foundIndex = blocksCopy[key].findIndex(
            (block) => block.id === id,
          )

          const payload = {
            ...value,
            id,
            type: 'category',
          }

          if (foundIndex > -1) {
            blocksCopy[key][foundIndex] = payload
          } else {
            blocksCopy[key].push(payload)
          }
        } else {
          blocksCopy[key].push({
            ...value,
            id: blockId,
            type: 'category',
            children: [],
          })
        }
      })

      commit('SET_BLOCKS', blocksCopy)
      commit('SET_CATEGORY_MODAL_VISIBLE', false)
      commit('SET_SELECTED_CATEGORY_DATA', null)
    },
    onCategoryDelete({ commit, state }, id) {
      if (!id) return

      function removeBlockById(items, id) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].id === id) {
            items.splice(i, 1)
            return true
          }
          if (items[i].children?.length) {
            const removed = removeBlockById(items[i].children, id)
            if (removed) return true
          }
        }
        return false
      }

      const currentBlocks = JSON.parse(
        JSON.stringify(state.content.blocks[state.selectedLanguage]),
      )
      removeBlockById(currentBlocks, id)

      const blocksCopy = JSON.parse(JSON.stringify(state.content.blocks))
      blocksCopy[state.selectedLanguage] = currentBlocks

      commit('SET_BLOCKS', blocksCopy)
      commit('SET_CATEGORY_MODAL_VISIBLE', false)
      commit('SET_SELECTED_CATEGORY_DATA', null)
    },
    onWidgetSubmit({ commit, state }, { data, id }) {
      const blocksCopy = JSON.parse(JSON.stringify(state.content.blocks))

      const blockId = uuidv1()
      Object.entries(data).forEach(([lang, value]) => {
        if (!blocksCopy[lang]) {
          blocksCopy[lang] = []
        }

        if (id) {
          // eslint-disable-next-line no-inner-declarations
          function updateWidget(items, id, data) {
            if (!items.length) {
              return false
            }

            for (let i = 0; i < items.length; i++) {
              if (items[i].type === 'widget' && items[i].id === id) {
                items[i] = { ...items[i], ...data }

                return true
              }

              if (items[i].children?.length) {
                const updated = updateWidget(items[i].children, id, data)
                if (updated) return true
              }
            }

            return false
          }

          const foundAndUpdated = updateWidget(blocksCopy[lang], id, value)

          if (!foundAndUpdated) {
            blocksCopy[lang].push({
              ...value,
              id,
              type: 'widget',
            })
          }
        } else {
          blocksCopy[lang].push({
            ...value,
            id: blockId,
            type: 'widget',
          })
        }
      })

      commit('SET_BLOCKS', blocksCopy)
      commit('SET_WIDGET_MODAL_VISIBLE', false)
      commit('SET_SELECTED_WIDGET_DATA', null)
    },
    onWidgetDelete({ commit, state }, id) {
      if (!id) return

      function removeBlockById(items, id) {
        for (let i = 0; i < items.length; i++) {
          if (items[i].id === id) {
            items.splice(i, 1)
            return true
          }
          if (items[i].children?.length) {
            const removed = removeBlockById(items[i].children, id)
            if (removed) return true
          }
        }
        return false
      }

      const currentBlocks = JSON.parse(
        JSON.stringify(state.content.blocks[state.selectedLanguage]),
      )
      removeBlockById(currentBlocks, id)

      const blocksCopy = JSON.parse(JSON.stringify(state.content.blocks))
      blocksCopy[state.selectedLanguage] = currentBlocks

      commit('SET_BLOCKS', blocksCopy)
      commit('SET_WIDGET_MODAL_VISIBLE', false)
      commit('SET_SELECTED_WIDGET_DATA', null)
    },
    onEditCategory({ commit, state, rootGetters }, id) {
      const langs = rootGetters['langs']?.langData

      const foundCategory = langs.reduce((acc, lang) => {
        const category = state.content.blocks?.[lang.name]?.find(
          (c) => c.id === id,
        )

        if (category) {
          return {
            ...acc,
            [lang.name]: category,
          }
        }

        return {
          ...acc,
        }
      }, {})

      commit(
        'SET_SELECTED_CATEGORY_DATA',
        JSON.parse(JSON.stringify(foundCategory)),
      )
      commit('SET_CATEGORY_MODAL_VISIBLE', true)
    },
    onEditWidget({ commit, state, rootGetters }, id) {
      const langs = rootGetters['langs']?.langData

      const foundWidget = langs.reduce((acc, lang) => {
        const widget = findBlockById(state.content.blocks?.[lang.name], id)

        if (widget) {
          return {
            ...acc,
            [lang.name]: widget,
          }
        }

        return {
          ...acc,
        }
      }, {})

      commit(
        'SET_SELECTED_WIDGET_DATA',
        JSON.parse(JSON.stringify(foundWidget)),
      )
      commit('SET_WIDGET_MODAL_VISIBLE', true)
    },
    onAddEvents({ commit, state }, { data, id }) {
      const blocksCopy = JSON.parse(JSON.stringify(state.content.blocks))
      const categoryBlock = findBlockById(
        blocksCopy[state.selectedLanguage],
        id,
      )

      const newTournamentBlocks = []

      Object.values(data.champs).map((c) => {
        const newMatchesInChamp = c.matches.map((m) => m.id)

        categoryBlock.children = categoryBlock.children.filter(
          (child) =>
            !(child.type === 'match' && newMatchesInChamp.includes(child.id)),
        )

        const existingTournamentBlock = findBlockById(
          categoryBlock.children,
          c.champ.id,
          'championshipId',
        )

        if (existingTournamentBlock) {
          const alreadySelectedMatches = existingTournamentBlock.children
            .filter((b) => b.type === 'match')
            ?.map((m) => m.id)
          c.matches
            .filter((m) => !alreadySelectedMatches.includes(m.id))
            .forEach((match) => {
              existingTournamentBlock.children.push({
                type: 'match',
                id: match.id,
                date: match.starts_at,
                name: match.name,
                championshipId: c.champ.id,
                sportId: c.champ.sportId,
              })
            })
        } else {
          newTournamentBlocks.push({
            id: uuidv1(),
            type: 'tournament',
            name: c.champ?.name,
            championshipId: c.champ.id,
            children: c.matches.map((match) => ({
              type: 'match',
              id: match.id,
              date: match.starts_at,
              name: match.name,
              championshipId: c.champ.id,
              sportId: c.champ.sportId,
            })),
          })
        }
      })

      categoryBlock.children.push(...newTournamentBlocks)

      commit('SET_BLOCKS', blocksCopy)
    },
    closeCategoryModal({ commit }) {
      commit('SET_CATEGORY_MODAL_VISIBLE', false)
      commit('SET_SELECTED_CATEGORY_DATA', null)
    },
    closeWidgetModal({ commit }) {
      commit('SET_WIDGET_MODAL_VISIBLE', false)
      commit('SET_SELECTED_WIDGET_DATA', null)
    },
  },
  getters: {
    selectedLanguage: (state) => state.selectedLanguage,

    header: (state) => state.header,
    isHeaderEditModalOpen: (state) => state.isHeaderEditModalOpen,

    blocks: (state) => state.content.blocks,
    selectedMatches: (state, getters, rootState, rootGetters) => {
      const langs = rootGetters['langs']?.langData.map((lang) => lang.name)

      function dfs(list, acc) {
        for (const item of list) {
          if (item.type === 'match') {
            acc.push(item)
          }

          if (item.children) {
            dfs(item.children, acc)
          }
        }
      }

      const result = langs.reduce((acc, lang) => {
        if (state.content.blocks[lang]) {
          const matches = []

          dfs(state.content.blocks[lang], matches)

          return {
            ...acc,
            [lang]: [...matches],
          }
        }

        return {
          ...acc,
        }
      }, {})

      return result
    },
    isCategoryModalVisible: (state) => state.isCategoryModalVisible,
    isWidgetModalVisible: (state) => state.isWidgetModalVisible,
    isEventsModalVisible: (state) => state.isEventsModalVisible,
    selectedCategoryData: (state) => state.selectedCategoryData,
    selectedWidgetData: (state) => state.selectedWidgetData,
  },
}
